import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PersonalAccountOpenPages, usePersonalAccountOpenPageTypeStore } from 'views/pages/accountOpen/personal/hooks';
import { useEffect } from 'react';
import { usePersonalAccountEstablishStore } from '../hooks';
import { ErrorWord, TemplateProps } from '../common';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { PageStep } from 'views/pages/accountInfo/hooks';
import { subYears, format  } from 'date-fns';
import {
  AdvanceQuestionMutationVariables,
  useChangeAccountInfoMutation,
  useAdvanceQuestionMutation,
} from 'views/utils/graphql/graphql';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import useOnceSubmit from 'views/utils/useOnceSubmit';

export enum Nationality {
  Japan = 1,
  Other,
}

export enum CountryOfResidence {
  Yes = 1,
  No,
}

export type AdvanceQuestionType = {
  uniqueId: string | null;
  nationality: boolean | null;
  countryOfResidence: boolean | null;
  birthdate: Date | null;
  identificationQuestion: boolean | null;
  mailAddressQuestion: string | null;
  selfieQuestion: boolean | null;
  personalInformation: boolean;
  generalTermsCondition: boolean;
  confirmResponsibility: boolean;
};

export type DisplayItemProps = {
  label: string;
  value: string;
  hidden?: boolean;
};

export const usePersonalAccountAdvancedQuestion = ({ isUpdate, setPageStep }: TemplateProps) => {
  const { param, add } = usePersonalAccountEstablishStore();
  const { setAccountOpenPageType } = usePersonalAccountOpenPageTypeStore();
  const [_advanceQuestionResponse, advanceQuestion] = useAdvanceQuestionMutation();
  const [_changeAccountResponse, changeAccount] = useChangeAccountInfoMutation();
  const { showError } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advanceQuestionValidation = Yup.object().shape({
    nationality: !isUpdate
      ? Yup.bool().required(ErrorWord.SELECT).isTrue('国籍が日本以外の方は、口座開設をお受けできません。')
      : Yup.bool().nullable(),
    countryOfResidence: !isUpdate
      ? Yup.bool()
          .required(ErrorWord.SELECT)
          .isTrue('日本国内に住所を有しない非居住者の方は、口座開設をお受けできません。')
      : Yup.bool().nullable(),
    birthdate: !isUpdate
      ? Yup.date()
          .required(ErrorWord.Date)
          .max(subYears(new Date(), 18), '18歳未満の方は、口座開設をお受けできません。')
      : Yup.date().nullable(),
    identificationQuestion: Yup.bool().required(ErrorWord.SELECT),
    mailAddressQuestion: !isUpdate
      ? Yup.string().email(ErrorWord.EMAIL).required(ErrorWord.EMAIL_NONE)
      : Yup.bool().nullable(),
    selfieQuestion: Yup.bool().required(ErrorWord.SELECT),
  });

  const initialValues: AdvanceQuestionType = {
    uniqueId: param.uniqueId ?? null,
    nationality: param.nationality ?? null,
    countryOfResidence: param.countryOfResidence ?? null,
    birthdate: param.birthdate ?? null,
    identificationQuestion: param.identificationQuestion ?? null,
    mailAddressQuestion: param.mailAddressQuestion ?? null,
    selfieQuestion: param.selfieQuestion ?? null,
    personalInformation: param.personalInformation ?? false,
    generalTermsCondition: param.generalTermsCondition ?? false,
    confirmResponsibility: param.confirmResponsibility ?? false,
  };

  const executeOpenAccount = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const data: AdvanceQuestionMutationVariables = {
          input: {
            PersonalAccount: {
              AdvanceNationality: formik.values.nationality ?? false,
              AdvanceCountryOfResidence: formik.values.countryOfResidence ?? false,
              Birthdate: format(formik.values.birthdate!, "yyyy/MM/dd") ?? '',
              HaveEKYCDocument: formik.values.identificationQuestion ?? false,
              CanTakePicture: formik.values.selfieQuestion ?? false,
              DealerMailAddress: formik.values.mailAddressQuestion ?? '',
            },
          },
        };
        const openAccountData = await advanceQuestion(data);
        if (!openAccountData?.data) return;

        if (openAccountData.data.AdvanceQuestion?.Code === 0) {
          setAccountOpenPageType(PersonalAccountOpenPages.completeSendMail);
        } else {
          showError(openAccountData.data.AdvanceQuestion?.Message);
        }
      },
    },
    [showError],
  );

  const executeUpdateAccount = () => {
    add({
      ...param,
      ...formik.values,
    });
    setPageStep && setPageStep(PageStep.UPDATE);
  }

  const formik = useFormik<AdvanceQuestionType>({
    initialValues,
    onSubmit: async () => {
      if (isUpdate) {
        await executeUpdateAccount();
      } else {
        await executeOpenAccount();
      }
    },
    validationSchema: advanceQuestionValidation,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const nationalityProps = {
    selection: {
      name: 'NationalityQuestion',
      selected: formik.values.nationality,
      onChange: async (value: boolean) => {
        await formik.setFieldValue('nationality', value);
        formik.validateField('nationality');
      },
      disabled: formik.values.nationality,
      labelYes: '日本',
      labelNo: '日本以外',
      error: formik.errors.nationality,
    },
  };
  const countryOfResidenceProps = {
    selected: formik.values.countryOfResidence,
    onChange: async (value: boolean) => {
      await formik.setFieldValue('countryOfResidence', value);
      formik.validateField('countryOfResidence');
    },
    error: formik.errors.countryOfResidence,
  };

  const birthdayProps = {
    date: formik.values.birthdate,
    name: 'birthDate',
    openToDate: subYears(new Date(), 18),
    minDate: new Date('1700/1/1'),
    maxDate: new Date(),
    onChange: async (value: any) => {
      await formik.setFieldValue('birthdate', value);
      formik.validateField('birthdate');
    },
    onBlur: formik.handleBlur,
    isWeekEndDisable: false,
    error: formik.errors.birthdate,
  };

  const identificationQuestionProps = {
    selected: formik.values.identificationQuestion,
    onChange: (value: boolean) => formik.setFieldValue('identificationQuestion', value),
    error: formik.errors.identificationQuestion,
  };

  const mailAddressQuestionProps = {
    selected: formik.values.mailAddressQuestion,
    onChange: (value: string) => formik.setFieldValue('mailAddressQuestion', value),
    error: formik.errors.mailAddressQuestion,
  };

  const selfieQuestionProps = {
    selected: formik.values.selfieQuestion,
    onChange: (value: boolean) => formik.setFieldValue('selfieQuestion', value),
    error: formik.errors.selfieQuestion,
  };

  const personalInformationProps = {
    checked: formik.values.personalInformation,
    setChecked: (value: boolean) => formik.setFieldValue('personalInformation', value),
  };

  const generalTermsConditionProps = {
    checked: formik.values.generalTermsCondition,
    setChecked: (value: boolean) => formik.setFieldValue('generalTermsCondition', value),
  };
  const confirmResponsibilityProps = {
    checked: formik.values.confirmResponsibility,
    setChecked: (value: boolean) => formik.setFieldValue('confirmResponsibility', value),
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    nationalityProps,
    countryOfResidenceProps,
    birthdayProps,
    identificationQuestionProps,
    mailAddressQuestionProps,
    selfieQuestionProps,
    personalInformationProps,
    generalTermsConditionProps,
    confirmResponsibilityProps,
  };
};
